import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { delay } from '~/common/utils/async.util';
import { getUsageHistoriesApi } from '~/features/usage-history/api/get-usage-histories.api';

import type { Nullable } from '~/common/types/nullable.type';
import type { PaginationMeta } from '~/common/types/pagination.type';
import type {
  UsageHistoryItem,
  UsageHistoryListParams,
} from '~/features/usage-history/types/usage-history.type';

export interface UsageHistoryState {
  isLoading: boolean;
  items: UsageHistoryItem[];
  params: Nullable<UsageHistoryListParams>;
  meta: PaginationMeta | null;
}

const initialState: UsageHistoryState = {
  isLoading: false,
  items: [],
  params: null,
  meta: null,
};

export const fetchUsageHistories = createAsyncThunk(
  'usageHistory/fetchUsageHistories',
  async (params: UsageHistoryListParams | undefined, { rejectWithValue }) => {
    try {
      const [{ data }] = await Promise.all([getUsageHistoriesApi(params), delay(200)]);
      return { data, params };
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const UsageHistorySlice = createSlice({
  name: 'usageHistory',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchUsageHistories.pending, (state, action) => {
        state.isLoading = true;
        state.params = action.meta.arg || null;
      })
      .addCase(fetchUsageHistories.fulfilled, (state, action) => {
        const page = action.payload.params?.page ?? 1;
        if (page > 1) {
          state.items.push(...action.payload.data.data);
        } else {
          state.items = action.payload.data.data;
        }
        state.meta = action.payload.data.meta;
        state.isLoading = false;
      })
      .addCase(fetchUsageHistories.rejected, state => {
        state.isLoading = false;
        state.items = [];
        state.meta = null;
      });
  },
});

export default UsageHistorySlice;
